// src/pages/CongressMemberPage.jsx
// Congressional Trade Tracker — Member Detail Subpage.
// Design doc: docs/Design Document/CONGRESSIONAL-PAGE-REDESIGN-DESIGN.md, Section 4.
//
// Routed page (/congress/member/:bioguideId?chamber=), NOT a modal — matches the
// codebase's existing per-entity detail-page pattern (/stocks/:ticker,
// /chart/:ticker), per Section 4.1's own reasoning: shareable/bookmarkable,
// consistent with how the rest of the site treats "click an entity, land on
// its own page," and avoids the state-loss problem of a modal stacked over
// Tab 1's paginated/filtered Trade Feed.
//
// Entry points (all wired in CongressPage.jsx): Trade Feed member name link,
// Member Profiles row click, Committee View roster row click.
//
// Two endpoints power this page:
//   GET /api/congress/members/:bioguideId?chamber=       — member + committees + terms + tradeStats
//   GET /api/congress/members/:bioguideId/trades?chamber= — exact composite-key trade history,
//     NOT the same as /trades?member=<name> (fragile LIKE match) — see congress.js's own header
//     note on this endpoint for why the exact key matters here.
//
// Guest lock: SAME convention as Tab 1 (GUEST_LIMIT rows on page 1 only, real
// total shown) — reused, not reinvented, per the design doc's own "one
// established pattern, not a new shape per surface" discipline.
//
// Stock performance section is a "coming soon" placeholder (Phase 2, gated on
// the Stooq pipeline — see design doc Section 3) — stated honestly, not hidden,
// same discipline the original Committee View banner already used for its own
// honest gap.

import { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, ExternalLink, Crown, Phone, MapPin, Landmark, TrendingUp, ArrowLeftRight, ChevronDown, ChevronRight } from 'lucide-react';
import { API_URL } from '../styles/shared';
import MemberPhoto from '../components/congressional/MemberPhoto';
import TickerStatusBadge from '../components/congressional/TickerStatusBadge';
import { CATEGORY_GROUP_MAP, CATEGORY_GROUP_LABELS, CATEGORY_GROUP_ORDER } from '../components/congressional/congressShared';

// FETCH_ALL_TRADES_SIZE — replaces the old PAGE_SIZE=25 pagination for Trade
// History (owner request: group by Purchase/Sale × category instead of
// paginating). Matches congressMembers.js's own MAX_MEMBER_TRADES_PAGE_SIZE
// cap for this endpoint — a genuinely bounded, single-member data set, not
// Trade Feed's site-wide scale, so fetching "everything" here is safe.
const FETCH_ALL_TRADES_SIZE = 1000;
const PARTY_COLORS = { D: '#00d4ff', R: '#ef4444', I: '#a78bfa' };
const TYPE_COLORS = { Purchase: '#00ff88', Sale: '#ef4444', Exchange: '#f59e0b', Hold: '#64748b' };

// OWNER conviction multiplier — same values congress.js's own moat-data
// comment documents (SELF=1.0 · JOINT=0.85 · SPOUSE=0.70 · DEPENDENT=0.60).
// Duplicated here as a small, self-contained display map — not imported from
// a shared constants file since none currently exists for this value; if one
// is created later, this should switch to importing it rather than keeping
// its own copy.
const OWNER_LABELS = {
  SELF: 'Self (1.0x conviction)',
  JOINT: 'Joint (0.85x conviction)',
  SPOUSE: 'Spouse (0.70x conviction)',
  DEPENDENT: 'Dependent (0.60x conviction)',
  OTHER: 'Other',
};

// REASON values from inactive_congress_members (confirmed against DB_SCHEMA.md,
// not guessed): RETIRED / LOST_ELECTION / RESIGNED / DECEASED / DEPARTED_CONGRESS.
// Human-readable labels for the "no longer serving" status line.
const REASON_LABELS = {
  RETIRED: 'Retired',
  LOST_ELECTION: 'Lost re-election',
  RESIGNED: 'Resigned',
  DECEASED: 'Deceased',
  DEPARTED_CONGRESS: 'No longer serving',
};

// Same fix as CongressPage.jsx's own fmtDate — a bare 'YYYY-MM-DD' string
// parsed via `new Date()` shifts a day back in some US timezones once
// rendered through toLocaleDateString(). Parsed directly from the string's
// own digits instead, so it can't happen regardless of viewer timezone.
const MONTH_ABBR = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const fmtDate = (d) => {
  if (!d) return '—';
  const m = String(d).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '—';
  return `${parseInt(m[3], 10)} ${MONTH_ABBR[parseInt(m[2], 10) - 1]} ${m[1]}`;
};

// Matches InsightsTab.jsx's own fmtUSD — same rounding, same display shape,
// kept local here rather than imported since congressShared.jsx doesn't
// currently export it either (same "not worth a shared module for one small
// helper" reasoning as elsewhere on this page).
function fmtUSD(n) {
  if (n == null) return '—';
  return '$' + Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 });
}

const Card = ({ children, style }) => (
  <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '1rem 1.25rem', marginBottom: '0.75rem', ...style }}>{children}</div>
);
const smallBtn = (color) => ({ padding: '0.4rem 0.9rem', background: color + '1a', border: '1px solid ' + color + '4d', borderRadius: '6px', color, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 600 });
const authHeaders = (token) => (token ? { Authorization: 'Bearer ' + token } : {});

export default function CongressMemberPage({ bioguideId, chamber, isLoggedIn, token, onNavigate, onRequestInvite }) {
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(true);
  const [detailError, setDetailError] = useState('');

  const [trades, setTrades] = useState([]);
  const [tradesTotal, setTradesTotal] = useState(0);
  const [tradesLoading, setTradesLoading] = useState(true);
  const [tradesError, setTradesError] = useState('');
  const [isGuest, setIsGuest] = useState(true);

  // Per-member Insights widgets (owner request) — same 4 views as the
  // site-wide Insights tab, scoped down to just this member via the
  // bioguideId+chamber filter added to congressInsights.js's endpoints this
  // pass. Kept separate from the trades/detail state above since these load
  // independently and shouldn't block the rest of the page if slow.
  const [topTickers, setTopTickers] = useState([]);
  const [topVolume, setTopVolume] = useState([]);
  const [filingLag, setFilingLag] = useState(null);
  const [convergence, setConvergence] = useState([]);
  const [insightsLoading, setInsightsLoading] = useState(true);

  // expandedGroups (owner request — replaces pagination with grouped,
  // collapsible sections): tracks which Purchase/Sale × category sections
  // are open, keyed as `${type}:${groupKey}`. Stocks & Options starts
  // expanded in both columns (the largest group for most members, per real
  // data seen so far); the other 3 start collapsed — balances "see
  // something useful immediately" against "don't dump everything at once,"
  // which was the actual complaint this replaces (too many page navigations).
  // FIXED (real bug, caught by the owner's own CI=true build): this useState
  // was originally placed AFTER the early-return guard below
  // (`if (!bioguideId || !chamber) return ...`) — a genuine Rules of Hooks
  // violation, since hooks must run unconditionally, in the same order,
  // every render. Moved up here with every other useState call in this
  // component, before any conditional return.
  const [expandedGroups, setExpandedGroups] = useState({ 'Purchase:stocks_options': true, 'Sale:stocks_options': true });

  const loadDetail = useCallback(async () => {
    if (!bioguideId || !chamber) return;
    setDetailLoading(true);
    setDetailError('');
    try {
      const r = await fetch(`${API_URL}/api/congress/members/${bioguideId}?chamber=${chamber}`, { headers: authHeaders(token) });
      const d = await r.json().catch(() => ({ success: false }));
      if (!d.success) setDetailError(d.message || 'Could not load this member.');
      else setDetail(d.data);
    } catch (e) {
      setDetailError('Could not reach the server.');
    }
    setDetailLoading(false);
  }, [bioguideId, chamber, token]);

  const loadTrades = useCallback(async () => {
    if (!bioguideId || !chamber) return;
    setTradesLoading(true);
    setTradesError('');
    try {
      const qs = new URLSearchParams({ chamber, page: '1', pageSize: String(FETCH_ALL_TRADES_SIZE) });
      const r = await fetch(`${API_URL}/api/congress/members/${bioguideId}/trades?${qs.toString()}`, { headers: authHeaders(token) });
      const d = await r.json().catch(() => ({ success: false }));
      if (!d.success) {
        setTradesError(d.message || 'Could not load trade history.');
      } else {
        setTrades(d.data.rows || []);
        setTradesTotal(d.data.total || 0);
        setIsGuest(!!d.data.isGuest);
      }
    } catch (e) {
      setTradesError('Could not reach the server.');
    }
    setTradesLoading(false);
  }, [bioguideId, chamber, token]);

  const loadInsights = useCallback(async () => {
    if (!bioguideId || !chamber) return;
    setInsightsLoading(true);
    try {
      const headers = authHeaders(token);
      // top-tickers/top-volume/filing-lag: window=all (this member's full
      // history, not a 90-day slice — a member-level summary should reflect
      // everything on record). convergence's own 'window' param means
      // something different (day-proximity between two trades, not a
      // lookback cutoff — see congressInsights.js's own handler), so it can't
      // share this same query string; built separately below.
      const memberQs = `bioguideId=${bioguideId}&chamber=${chamber}&window=all`;
      const [tickersR, volumeR, lagR, convergenceR] = await Promise.all([
        fetch(`${API_URL}/api/congress/insights/top-tickers?${memberQs}&limit=8`, { headers }),
        fetch(`${API_URL}/api/congress/insights/top-volume?${memberQs}&limit=8`, { headers }),
        fetch(`${API_URL}/api/congress/insights/filing-lag?groupBy=overall&${memberQs}`, { headers }),
        fetch(`${API_URL}/api/congress/insights/convergence?bioguideId=${bioguideId}&chamber=${chamber}&window=14&limit=8`, { headers }),
      ]);
      const [tickersD, volumeD, lagD, convergenceD] = await Promise.all([
        tickersR.json(), volumeR.json(), lagR.json(), convergenceR.json(),
      ]);
      if (tickersD.success) setTopTickers(tickersD.data.rows || []);
      if (volumeD.success) setTopVolume(volumeD.data.rows || []);
      if (lagD.success) setFilingLag(lagD.data.rows[0] || null);
      if (convergenceD.success) setConvergence(convergenceD.data.rows || []);
    } catch (e) {
      // Silent — these are supplementary widgets, not the page's core content.
      // The Trade History section below still loads and works independently.
    }
    setInsightsLoading(false);
  }, [bioguideId, chamber, token]);

  useEffect(() => { loadDetail(); }, [loadDetail]);
  useEffect(() => { loadTrades(); }, [loadTrades]);
  useEffect(() => { loadInsights(); }, [loadInsights]);

  if (!bioguideId || !chamber) {
    return (
      <div style={{ padding: '2rem 1rem', textAlign: 'center', color: '#64748b' }}>
        No member selected. <button onClick={() => onNavigate('congress')} style={smallBtn('#00d4ff')}>← Back to Congressional Trades</button>
      </div>
    );
  }

  const hiddenCount = isGuest ? Math.max(0, tradesTotal - trades.length) : 0;

  const toggleGroup = (key) => setExpandedGroups(prev => ({ ...prev, [key]: !prev[key] }));

  // groupTrades — Purchase/Sale columns, each split into the 4 category
  // groups (CATEGORY_GROUP_ORDER) via CATEGORY_GROUP_MAP. Any
  // TRANSACTIONTYPE outside Purchase/Sale (Exchange, Hold — rare) falls into
  // its own 'Other' bucket rather than being silently dropped or forced into
  // one of the two main columns. Any ASSETCATEGORY not in CATEGORY_GROUP_MAP
  // (shouldn't happen — the backend allowlist enforces exactly 7 values, but
  // defensive rather than assuming) falls into 'uncategorized' rather than
  // vanishing from the page.
  function groupTrades(rows) {
    const result = { Purchase: {}, Sale: {}, Other: {} };
    for (const key of CATEGORY_GROUP_ORDER) { result.Purchase[key] = []; result.Sale[key] = []; result.Other[key] = []; }
    result.Purchase.uncategorized = []; result.Sale.uncategorized = []; result.Other.uncategorized = [];
    for (const row of rows) {
      const column = (row.TRANSACTIONTYPE === 'Purchase' || row.TRANSACTIONTYPE === 'Sale') ? row.TRANSACTIONTYPE : 'Other';
      const groupKey = CATEGORY_GROUP_MAP[row.ASSETCATEGORY] || 'uncategorized';
      result[column][groupKey].push(row);
    }
    return result;
  }
  const grouped = groupTrades(trades);
  const hasOtherColumn = grouped.Other && Object.values(grouped.Other).some(arr => arr.length > 0);

  return (
    <div style={{ padding: '2rem 1rem' }}>
    {/* FIXED (owner report — page using <60% of screen width): this used to
        hardcode maxWidth: '900px', a one-off narrower value invented for
        this page alone rather than using the site's own established width
        policy. shared.js's s.container (clamp(1400px, 96vw, 2000px)) is
        that policy — CongressPage.jsx already relies on it implicitly by
        setting no local override; this page now does the same. */}
      <button onClick={() => onNavigate('congress')} style={{ ...smallBtn('#64748b'), display: 'inline-flex', alignItems: 'center', gap: '0.4rem', marginBottom: '1.25rem' }}>
        <ArrowLeft size={13} /> Back to Congressional Trades
      </button>

      {detailLoading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#64748b' }}>Loading...</div>
      ) : detailError || !detail ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#ef4444' }}>{detailError || 'Member not found.'}</div>
      ) : (
        <>
          {/* ── Header ── */}
          <Card>
            <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
              <MemberPhoto bioguideId={detail.member.BIOGUIDEID} size={110} />
              <div style={{ flex: '1 1 260px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <h1 style={{ margin: 0, color: '#f1f5f9', fontSize: '1.4rem', fontWeight: 700 }}>
                    {detail.member.FIRSTNAME} {detail.member.LASTNAME}
                  </h1>
                  {detail.member.ISCOMMITTEECHAIR === 1 && <Crown size={16} color="#ffd700" />}
                  <span style={{ color: PARTY_COLORS[detail.member.PARTY] || '#94a3b8', fontWeight: 700 }}>{detail.member.PARTY}</span>
                  {/* Active/inactive status (owner request) — ISACTIVE now comes back
                      from congressMembers.js's own fallback lookup (active table first,
                      inactive_congress_members second) rather than assuming everyone
                      found on this page is currently serving. */}
                  {detail.member.ISACTIVE === 1 ? (
                    <span style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 700, background: 'rgba(0,255,136,0.12)', color: '#00ff88' }}>Active</span>
                  ) : (
                    <span title={detail.member.DEPARTUREDATE ? `Left Congress ${detail.member.DEPARTUREDATE}` : ''} style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 700, background: 'rgba(148,163,184,0.12)', color: '#94a3b8' }}>
                      {REASON_LABELS[detail.member.REASON] || 'No longer serving'}
                    </span>
                  )}
                </div>
                <p style={{ margin: '0.35rem 0', color: '#64748b', fontSize: '0.85rem' }}>
                  {detail.member.CHAMBER} · {detail.member.STATE}{detail.member.DISTRICT ? `-${detail.member.DISTRICT}` : ''}
                  {detail.member.TENUREYEARS != null ? ` · ${detail.member.TENUREYEARS} years` : ''}
                  {detail.member.STATERANK ? ` · ${detail.member.STATERANK}` : ''}
                  {detail.member.ISACTIVE !== 1 && detail.member.LASTSERVEDYEAR ? ` · last served ${detail.member.LASTSERVEDYEAR}` : ''}
                </p>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '0.8rem', color: '#94a3b8', marginTop: '0.5rem' }}>
                  {detail.member.WEBSITEURL && (
                    <a href={detail.member.WEBSITEURL} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', color: '#00d4ff', textDecoration: 'none' }}>
                      Official website <ExternalLink size={11} />
                    </a>
                  )}
                  {detail.member.PHONE && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}><Phone size={11} /> {detail.member.PHONE}</span>
                  )}
                  {detail.member.OFFICEADDRESS && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}><MapPin size={11} /> {detail.member.OFFICEADDRESS}</span>
                  )}
                </div>
              </div>
            </div>
          </Card>

          {/* ── Trade activity summary ── */}
          <Card>
            <p style={{ margin: '0 0 0.5rem', color: '#94a3b8', fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase' }}>Trade activity</p>
            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', fontSize: '0.85rem', color: '#e2e8f0' }}>
              <span>{detail.tradeStats.TOTALTRADES || 0} total trades</span>
              <span>{detail.tradeStats.PURCHASES || 0} purchases</span>
              <span>{detail.tradeStats.SALES || 0} sales</span>
              <span>Avg filing lag: {detail.tradeStats.AVGFILINGLAGDAYS != null ? `${detail.tradeStats.AVGFILINGLAGDAYS}d` : '—'}</span>
              <span>Last trade: {fmtDate(detail.tradeStats.LASTTRADEDATE)}</span>
            </div>
          </Card>

          {/* ── Stock performance — Phase 2, gated on Stooq (design doc Section 3).
               FIXED (owner correction): the user-facing text below used to name the
               internal pipeline and explain why the feature isn't built yet — that's
               engineering detail that has no business being on a live customer page.
               The dev-facing "why" stays in this comment, not in the rendered text. ── */}
          <div style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', borderRadius: '8px', padding: '0.75rem 1rem', marginBottom: '0.75rem', color: '#f59e0b', fontSize: '0.8rem' }}>
            Stock performance (entry price → current price) — coming soon.
          </div>

          {/* ── Member Insights — same 4 views as the site-wide Insights tab,
               scoped to just this member (owner request). Convergence rows
               here are labeled the same "cluster context, not a signal" way
               as everywhere else this view appears on the site. ── */}
          {!insightsLoading && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '0.75rem', marginBottom: '0.75rem' }}>
              <Card style={{ marginBottom: 0 }}>
                <p style={{ margin: '0 0 0.6rem', color: '#94a3b8', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <TrendingUp size={12} /> Top traded tickers
                </p>
                {topTickers.length === 0 ? (
                  <p style={{ color: '#64748b', fontSize: '0.78rem', margin: 0 }}>No trades on record.</p>
                ) : topTickers.map((t, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.25rem 0', fontSize: '0.78rem', color: '#e2e8f0' }}>
                    <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{t.TICKER}</span>
                    <span style={{ color: '#64748b' }}>{t.TRADECOUNT} trades</span>
                  </div>
                ))}
              </Card>

              <Card style={{ marginBottom: 0 }}>
                <p style={{ margin: '0 0 0.6rem', color: '#94a3b8', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase' }}>Top buy / sell</p>
                {topVolume.length === 0 ? (
                  <p style={{ color: '#64748b', fontSize: '0.78rem', margin: 0 }}>No trades on record.</p>
                ) : topVolume.map((t, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.25rem 0', fontSize: '0.78rem', color: '#e2e8f0' }}>
                    <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{t.TICKER}</span>
                    <span style={{ color: t.TRANSACTIONTYPE === 'Sale' ? '#ef4444' : '#00ff88' }}>{t.TRANSACTIONTYPE} · {fmtUSD(t.ESTAMOUNT)}</span>
                  </div>
                ))}
              </Card>

              <Card style={{ marginBottom: 0 }}>
                <p style={{ margin: '0 0 0.6rem', color: '#94a3b8', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase' }}>Filing lag</p>
                {filingLag ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', fontSize: '0.78rem', color: '#e2e8f0' }}>
                    <span>Median: {filingLag.MEDIANLAGDAYS != null ? `${filingLag.MEDIANLAGDAYS}d` : '—'}</span>
                    <span>Avg: {filingLag.AVGLAGDAYS != null ? `${filingLag.AVGLAGDAYS}d` : '—'}</span>
                    <span style={{ color: '#64748b' }}>{filingLag.TRADECOUNT || 0} trades with a recorded lag</span>
                  </div>
                ) : (
                  <p style={{ color: '#64748b', fontSize: '0.78rem', margin: 0 }}>No filing lag data on record.</p>
                )}
              </Card>

              <Card style={{ marginBottom: 0 }}>
                <p style={{ margin: '0 0 0.4rem', color: '#94a3b8', fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <ArrowLeftRight size={12} /> Bipartisan convergence
                </p>
                <p style={{ margin: '0 0 0.5rem', color: '#64748b', fontSize: '0.7rem' }}>
                  Cluster context, not a trading signal.
                </p>
                {convergence.length === 0 ? (
                  <p style={{ color: '#64748b', fontSize: '0.78rem', margin: 0 }}>No cross-party convergence found.</p>
                ) : convergence.map((c, i) => {
                  // Show whichever side ISN'T this member — the counterparty.
                  const isA = c.MEMBERA && detail && (c.MEMBERA === detail.member.FIRSTNAME + ' ' + detail.member.LASTNAME);
                  const other = isA ? { name: c.MEMBERB, party: c.PARTYB, date: c.DATEB } : { name: c.MEMBERA, party: c.PARTYA, date: c.DATEA };
                  return (
                    <div key={i} style={{ padding: '0.3rem 0', fontSize: '0.78rem', color: '#e2e8f0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{c.TICKER}</span>
                      <span style={{ color: '#64748b' }}> with {other.name} ({other.party}), {fmtDate(other.date)}</span>
                    </div>
                  );
                })}
              </Card>
            </div>
          )}

          {/* ── Trade history — owner request: 2-column Purchase/Sale split,
               each grouped into 4 collapsible category sections instead of
               a flat paginated list. Real fix for "too many page
               navigations": since this endpoint now fetches a member's
               FULL trade history in one request (bounded per-member set,
               not Trade Feed's site-wide scale), there's no pagination left
               to navigate — sections collapse/expand instead. ── */}
          <Card>
            <p style={{ margin: '0 0 0.75rem', color: '#94a3b8', fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase' }}>
              Trade history {tradesTotal > 0 && `(${tradesTotal.toLocaleString()})`}
            </p>
            {tradesError && <div style={{ color: '#ef4444', fontSize: '0.82rem', marginBottom: '0.5rem' }}>{tradesError}</div>}
            {tradesLoading ? (
              <div style={{ textAlign: 'center', padding: '1.5rem', color: '#64748b' }}>Loading...</div>
            ) : trades.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '1.5rem', color: '#64748b' }}>No trades on record.</div>
            ) : (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: hasOtherColumn ? 'repeat(auto-fit, minmax(280px, 1fr))' : '1fr 1fr', gap: '1rem' }}>
                  {['Purchase', 'Sale', ...(hasOtherColumn ? ['Other'] : [])].map(columnType => {
                    const columnTotal = Object.values(grouped[columnType]).reduce((sum, arr) => sum + arr.length, 0);
                    return (
                      <div key={columnType}>
                        <p style={{ margin: '0 0 0.5rem', fontSize: '0.82rem', fontWeight: 700, color: TYPE_COLORS[columnType] || '#94a3b8' }}>
                          {columnType} ({columnTotal})
                        </p>
                        {[...CATEGORY_GROUP_ORDER, 'uncategorized'].map(groupKey => {
                          const rows = grouped[columnType][groupKey];
                          if (!rows || rows.length === 0) return null;
                          const label = CATEGORY_GROUP_LABELS[groupKey] || 'Uncategorized';
                          const expandKey = `${columnType}:${groupKey}`;
                          const isOpen = !!expandedGroups[expandKey];
                          return (
                            <div key={groupKey} style={{ marginBottom: '0.4rem' }}>
                              <button
                                onClick={() => toggleGroup(expandKey)}
                                style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', width: '100%', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '6px', padding: '0.4rem 0.6rem', cursor: 'pointer', color: '#e2e8f0', fontSize: '0.78rem', fontWeight: 600, fontFamily: 'inherit', textAlign: 'left' }}
                              >
                                {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                {label} ({rows.length})
                              </button>
                              {isOpen && (
                                <div style={{ paddingLeft: '0.5rem', borderLeft: '1px solid rgba(255,255,255,0.06)', marginTop: '0.2rem' }}>
                                  {rows.map(row => {
                                    const isRotation = row.TICKER === '--';
                                    return (
                                      <div key={row.ID} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '0.5rem 0' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
                                          <span style={{ color: '#f1f5f9', fontWeight: 700, fontFamily: 'monospace', fontSize: '0.85rem' }}>
                                            {isRotation ? <Landmark size={12} style={{ verticalAlign: '-2px', marginRight: '0.2rem', color: '#00d4ff' }} /> : null}
                                            {isRotation ? (row.ASSETCATEGORY || 'Rotation') : row.TICKER}
                                          </span>
                                          <TickerStatusBadge status={row.TICKERRESOLUTIONSTATUS} />
                                        </div>
                                        <div style={{ display: 'flex', gap: '0.9rem', flexWrap: 'wrap', fontSize: '0.72rem', color: '#64748b' }}>
                                          <span>{fmtDate(row.TRANSACTIONDATE)}</span>
                                          <span>{row.AMOUNTRANGE || '—'}</span>
                                          <span title={OWNER_LABELS[row.OWNER] || row.OWNER || 'SELF'}>{row.OWNER || 'SELF'}</span>
                                          {row.FILINGLAGDAYS != null && <span>Filed {row.FILINGLAGDAYS}d late</span>}
                                          {row.SOURCEURL && (
                                            <a href={row.SOURCEURL} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem', color: '#00d4ff', textDecoration: 'none' }}>
                                              Source filing <ExternalLink size={10} />
                                            </a>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                        {columnTotal === 0 && <p style={{ color: '#64748b', fontSize: '0.78rem', margin: 0 }}>None on record.</p>}
                      </div>
                    );
                  })}
                </div>

                {isGuest && hiddenCount > 0 && (
                  <div style={{ padding: '1.5rem 1rem', textAlign: 'center', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', marginTop: '0.75rem' }}>
                    <div style={{ fontSize: '0.9rem', color: '#f1f5f9', fontWeight: 700, marginBottom: '0.4rem' }}>
                      {hiddenCount.toLocaleString()} more trade{hiddenCount !== 1 ? 's' : ''} for members
                    </div>
                    <button onClick={onRequestInvite} style={{ padding: '0.55rem 1.4rem', borderRadius: '8px', border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: '#fff', fontWeight: 700, fontSize: '0.8rem', fontFamily: 'inherit' }}>
                      Request Invite → See Full History
                    </button>
                  </div>
                )}
              </>
            )}
          </Card>

          {/* ── Committee assignments ── */}
          <Card>
            <p style={{ margin: '0 0 0.5rem', color: '#94a3b8', fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase' }}>Committee assignments</p>
            {detail.committees.length === 0 ? (
              <p style={{ margin: 0, color: '#64748b', fontSize: '0.82rem' }}>No committee assignments on record.</p>
            ) : detail.committees.map((c, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', fontSize: '0.82rem', color: '#e2e8f0' }}>
                <span>{c.COMMITTEENAME}</span>
                <span style={{ color: c.COMMITTEEROLE === 'CHAIR' ? '#ffd700' : '#64748b' }}>{c.COMMITTEEROLE}</span>
              </div>
            ))}
          </Card>

          {/* ── Term history ── */}
          <Card>
            <p style={{ margin: '0 0 0.5rem', color: '#94a3b8', fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase' }}>Term history</p>
            {detail.terms.map((t, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.3rem 0', fontSize: '0.82rem', color: '#e2e8f0' }}>
                <span>Congress {t.CONGRESS} — {t.STARTYEAR}{t.ENDYEAR ? `–${t.ENDYEAR}` : (t.ISCURRENTTERM ? '–present' : '')}</span>
                <span style={{ color: '#64748b' }}>{t.PARTY} · {t.STATE}</span>
              </div>
            ))}
          </Card>
        </>
      )}
    </div>
  );
}
